/**
 * What an account is allowed to do, in one place.
 *
 * The Worker enforces these, the pricing page prints them and the CLI reports
 * them, so they cannot be spelled out separately anywhere else. `formatBytes`
 * is 1024-based, so the byte counts here are too — 10 GB really is 10 GiB, and
 * the page and the quota agree to the byte.
 *
 * Payment is deliberately not modelled. Nothing here talks to a payment
 * provider: an upgrade is Shubham reading a request, taking the money however
 * he took it, and moving the account with the admin API. `planNote` and
 * `planRenewsAt` on the user row are how that stays legible months later.
 */

import { formatBytes } from "./format.js";

const GB = 1024 * 1024 * 1024;

export type Plan = "free" | "pro";

export interface PlanSpec {
  id: Plan;
  label: string;
  /** US dollars per month. */
  price: number;
  /** Base quota before any pay-as-you-go add-on. */
  storageBytes: number;
  /** The largest single file this plan may upload. */
  maxFileBytes: number;
  /** How far back `/stats` reaches, in days; null is unlimited. */
  statsDays: number | null;
  tagline: string;
  /** Rendered in order on the pricing page. */
  features: string[];
}

export const PLANS: Record<Plan, PlanSpec> = {
  free: {
    id: "free",
    label: "Free",
    price: 0,
    storageBytes: 10 * GB,
    maxFileBytes: 2 * GB,
    statsDays: 90,
    tagline: "The whole product, capped only by what you store.",
    features: [
      "Unlimited links, public or private",
      "AI descriptions, alt text and semantic search",
      "The assistant, scoped to your own files",
      "Document and media conversions",
      "90 days of stats history",
    ],
  },
  pro: {
    id: "pro",
    label: "Pro",
    price: 10,
    storageBytes: 50 * GB,
    maxFileBytes: 5 * GB,
    statsDays: null,
    tagline: "Five times the room, the largest files the platform can take.",
    features: [
      "Everything on Free, uncapped",
      "Unlimited stats history",
      "Priority in the conversion sandbox",
      "Top up storage at $1 per 5 GB / month",
    ],
  },
};

export const PLAN_IDS: Plan[] = ["free", "pro"];

export const isPlan = (value: string): value is Plan => value === "free" || value === "pro";

/** Pay-as-you-go: one dollar a month buys this much extra room, on any plan. */
export const PAYG_BYTES_PER_DOLLAR = 5 * GB;
export const PAYG_PRICE = 1;

/** The stored shape the Worker reads off the user row. */
export interface AccountLimits {
  plan: Plan;
  /** Pay-as-you-go storage bought on top of the plan, in bytes. */
  bonusBytes: number;
  /** An explicit admin override of the whole quota. Null means "compute it". */
  quotaBytes: number | null;
}

/** What that account may actually do, once the plan and the add-ons are folded in. */
export interface Entitlements {
  plan: Plan;
  /** The total storage this account may hold. */
  storageBytes: number;
  maxFileBytes: number;
  statsDays: number | null;
  bonusBytes: number;
  /** True when an admin has pinned the quota by hand. */
  overridden: boolean;
}

export function entitlementsFor(limits: AccountLimits): Entitlements {
  const spec = PLANS[limits.plan] ?? PLANS.free;
  const bonusBytes = Math.max(0, limits.bonusBytes);
  const overridden = limits.quotaBytes !== null && limits.quotaBytes >= 0;
  return {
    plan: spec.id,
    storageBytes: overridden ? limits.quotaBytes! : spec.storageBytes + bonusBytes,
    maxFileBytes: spec.maxFileBytes,
    statsDays: spec.statsDays,
    bonusBytes,
    overridden,
  };
}

/**
 * Whether one more file of this size fits. Deleting a file frees its bytes
 * immediately, so this is always asked against live usage rather than against
 * a running tally.
 */
export function fitsInQuota(used: number, incoming: number, limits: Entitlements): boolean {
  return used + incoming <= limits.storageBytes;
}

/** Dollars per month for a given amount of pay-as-you-go storage. */
export const paygPriceFor = (bytes: number): number =>
  Math.ceil(bytes / PAYG_BYTES_PER_DOLLAR) * PAYG_PRICE;

/**
 * Sizes for display on the pricing page. `formatBytes` keeps one decimal so a
 * 1.4 GB file does not round to 1 GB, but a plan is always a round number and
 * "10.0 GB" reads like a measurement rather than an allowance.
 */
export const formatPlanBytes = (bytes: number): string =>
  formatBytes(bytes).replace(/\.0 /, " ");
